import { useState } from "react";
import "@/App.css";
import axios from "axios";
import { ShieldCheck, GraduationCap, Briefcase, Home, Menu, X, CheckCircle2, ArrowRight } from "lucide-react";
import { toast, Toaster } from "sonner";
import { BrowserRouter, Routes, Route, Link } from "react-router-dom";
import { SmsConsent } from "@/components/SmsConsent";
import Terms from "@/pages/Terms";
import Privacy from "@/pages/Privacy";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function HomePage() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [volunteerForm, setVolunteerForm] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    zip_code: "",
    interests: [],
    sms_opt_in: false
  });
  const [contactForm, setContactForm] = useState({
    name: "",
    email: "",
    subject: "",
    phone: "",
    message: "",
    sms_opt_in: false
  });
  const [loading, setLoading] = useState(false);

  const handleVolunteerSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/volunteer`, volunteerForm);
      toast.success("Thank you for volunteering! We'll be in touch soon.");
      setVolunteerForm({
        first_name: "",
        last_name: "",
        email: "",
        phone: "",
        zip_code: "",
        interests: [],
    sms_opt_in: false
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Something went wrong. Please try again.");
      console.error("Volunteer signup error:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, contactForm);
      toast.success("Message sent! We'll get back to you soon.");
      setContactForm({
        name: "",
        email: "",
        subject: "",
        phone: "",
    message: "",
    sms_opt_in: false
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Something went wrong. Please try again.");
      console.error("Contact form error:", error);
    } finally {
      setLoading(false);
    }
  };

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setMobileMenuOpen(false);
    }
  };

  return (
    <div className="App scroll-smooth">
      <Toaster position="top-center" richColors />
      
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md border-b border-gray-100">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <div className="flex items-center justify-between h-24 md:h-28">
            <div className="flex items-center md:-ml-4">
              <img 
                src="https://customer-assets.emergentagent.com/job_rivera-ny-senate/artifacts/aasxup92_7815758719719681036.png"
                alt="Orlando Rivera for New York State Senate - Working For Our Future"
                className="h-20 md:h-24 w-auto object-contain"
              />
            </div>
            
            <nav className="hidden md:flex items-center space-x-8">
              <button onClick={() => scrollToSection('about')} className="nav-link text-sm font-bold uppercase tracking-widest hover:text-campaignRed" data-testid="nav-about">About</button>
              <button onClick={() => scrollToSection('priorities')} className="nav-link text-sm font-bold uppercase tracking-widest hover:text-campaignRed" data-testid="nav-priorities">Priorities</button>
              <button onClick={() => scrollToSection('issues')} className="nav-link text-sm font-bold uppercase tracking-widest hover:text-campaignRed" data-testid="nav-issues">Issues</button>
              <button onClick={() => scrollToSection('volunteer')} className="nav-link text-sm font-bold uppercase tracking-widest hover:text-campaignRed" data-testid="nav-volunteer">Volunteer</button>
              <a href="https://secure.winred.com/orlando-for-senate/donate-today" target="_blank" rel="noopener noreferrer" className="cta-button px-6 py-3 bg-campaignRed text-white font-oswald uppercase tracking-widest text-sm font-bold" data-testid="nav-donate">Donate</a>
            </nav>
            
            <button 
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden p-2"
              data-testid="mobile-menu-button"
            >
              {mobileMenuOpen ? <X size={28} /> : <Menu size={28} />}
            </button>
          </div>
          
          {mobileMenuOpen && (
            <div className="md:hidden pb-4 space-y-4" data-testid="mobile-menu">
              <button onClick={() => scrollToSection('about')} className="block w-full text-left py-2 text-sm font-bold uppercase tracking-widest hover:text-campaignRed" data-testid="mobile-nav-about">About</button>
              <button onClick={() => scrollToSection('priorities')} className="block w-full text-left py-2 text-sm font-bold uppercase tracking-widest hover:text-campaignRed" data-testid="mobile-nav-priorities">Priorities</button>
              <button onClick={() => scrollToSection('issues')} className="block w-full text-left py-2 text-sm font-bold uppercase tracking-widest hover:text-campaignRed" data-testid="mobile-nav-issues">Issues</button>
              <button onClick={() => scrollToSection('volunteer')} className="block w-full text-left py-2 text-sm font-bold uppercase tracking-widest hover:text-campaignRed" data-testid="mobile-nav-volunteer">Volunteer</button>
              <button onClick={() => scrollToSection('contact')} className="block w-full text-left py-2 text-sm font-bold uppercase tracking-widest hover:text-campaignRed" data-testid="mobile-nav-contact">Contact</button>
            </div>
          )}
        </div>
      </header>

      {/* Hero Section */}
      <section 
        className="hero-section min-h-screen flex items-start relative overflow-hidden mt-24 md:mt-28"
        style={{ backgroundImage: `url('https://customer-assets.emergentagent.com/job_43db3809-a6c4-45ea-8f8f-7bfb185fd383/artifacts/ayx1x71c_5872968690461939137.jpeg')` }}
        data-testid="hero-section"
      >
        <div className="hero-overlay absolute inset-0"></div>
        <div className="container mx-auto px-4 md:px-8 max-w-7xl relative z-10 pt-12 md:pt-20">
          <div className="max-w-3xl">
            <h1 className="font-oswald text-5xl md:text-7xl lg:text-8xl font-bold tracking-tighter uppercase text-white mb-4" data-testid="hero-title">
              ORLANDO RIVERA
            </h1>
            <p className="font-oswald text-2xl md:text-4xl font-bold uppercase tracking-widest text-campaignRed mb-6" data-testid="hero-subtitle">
              NEW YORK STATE SENATE
            </p>
            <div className="font-inter text-xl md:text-2xl text-white mb-12 space-y-2" data-testid="hero-values">
              <p className="font-semibold">Common Sense.</p>
              <p className="font-semibold">Public Safety.</p>
              <p className="font-semibold">Opportunity.</p>
            </div>
            <div className="flex flex-col sm:flex-row gap-4">
              <a 
                href="https://secure.winred.com/orlando-for-senate/donate-today"
                target="_blank"
                rel="noopener noreferrer"
                className="cta-button px-8 py-4 bg-campaignRed text-white font-oswald uppercase tracking-widest text-sm font-bold text-center"
                data-testid="hero-donate-button"
              >
                Donate Now
              </a>
              <button 
                onClick={() => scrollToSection('volunteer')}
                className="cta-button px-8 py-4 bg-white text-campaignBlack border-2 border-white hover:bg-transparent hover:text-white font-oswald uppercase tracking-widest text-sm font-bold"
                data-testid="hero-volunteer-button"
              >
                Volunteer
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Priority Platform Section */}
      <section id="priorities" className="py-20 md:py-32 bg-white" data-testid="priorities-section">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <h2 className="font-oswald text-4xl md:text-6xl font-bold uppercase text-center mb-16" data-testid="priorities-heading">
            Our Priorities
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
            <div className="text-center space-y-4" data-testid="priority-item-1">
              <div className="priority-number">01</div>
              <h3 className="font-oswald text-2xl md:text-3xl font-bold uppercase">PUBLIC SAFETY</h3>
              <p className="text-gray-600">Supporting law enforcement and keeping our communities safe</p>
            </div>
            <div className="text-center space-y-4" data-testid="priority-item-2">
              <div className="priority-number">02</div>
              <h3 className="font-oswald text-2xl md:text-3xl font-bold uppercase">SCHOOL CHOICE</h3>
              <p className="text-gray-600">Empowering families with educational freedom and opportunity</p>
            </div>
            <div className="text-center space-y-4" data-testid="priority-item-3">
              <div className="priority-number">03</div>
              <h3 className="font-oswald text-2xl md:text-3xl font-bold uppercase">JOBS & GROWTH</h3>
              <p className="text-gray-600">Creating economic opportunity and cutting red tape for businesses</p>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-20 md:py-32 bg-gray-50 relative" data-testid="about-section">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <div className="relative bg-white p-8 md:p-12 shadow-lg">
            <div className="red-corner-accent"></div>
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 relative z-10">
              <div className="lg:col-span-2 flex flex-col items-center lg:items-start space-y-6">
                <img 
                  src="https://customer-assets.emergentagent.com/job_43db3809-a6c4-45ea-8f8f-7bfb185fd383/artifacts/v4lx2f0u_7900897050566912732.jpeg"
                  alt="Orlando Rivera"
                  className="w-full max-w-sm rounded-sm shadow-xl"
                  data-testid="about-photo"
                />
                <div className="w-full max-w-sm space-y-4 pt-4">
                  <h3 className="font-oswald text-xl font-bold uppercase border-l-4 border-campaignRed pl-4">Community Leadership</h3>
                  <ul className="space-y-2 text-sm text-gray-700 pl-4">
                    <li className="flex items-start gap-2">
                      <span className="text-campaignRed mt-1">▸</span>
                      <span>Chairman, Rochester Hispanic Business Association</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-campaignRed mt-1">▸</span>
                      <span>Commissioner, City of Rochester Civil Service Commission</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-campaignRed mt-1">▸</span>
                      <span>NAHREP Top 250 Award Recipient</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-campaignRed mt-1">▸</span>
                      <span>Multiple Sales Master Awards (GRAR)</span>
                    </li>
                  </ul>
                </div>
              </div>
              <div className="lg:col-span-3 space-y-6">
                <p className="text-sm font-bold uppercase tracking-widest text-campaignRed">Meet Orlando</p>
                <h2 className="font-oswald text-4xl md:text-5xl font-bold uppercase">A Voice for Our Community</h2>
                <div className="space-y-4 text-gray-700 leading-relaxed" data-testid="about-bio">
                  <p>
                    Orlando Rivera is a licensed real estate professional and longtime community leader with deep roots in Rochester. He currently serves as Chairman of the Rochester Hispanic Business Association, where he works to support small businesses, promote job creation, and expand economic opportunity across the region.
                  </p>
                  <p>
                    Orlando also serves as a Commissioner on the City of Rochester Civil Service Commission, helping ensure fair hiring practices, transparency, and accountability in city government. His record of service includes leadership and volunteer roles with the Knights of Columbus, the Cancer Support Community at Gilda's Club Rochester, and the American Red Cross of Western New York.
                  </p>
                  <p>
                    Recognized for both professional excellence and community impact, Orlando has earned multiple Sales Master Awards from the Greater Rochester Association of Realtors and has received national recognition from the National Association of Hispanic Real Estate Professionals as a recipient of its prestigious Top 250 award, honoring the top Hispanic real estate professionals in the United States.
                  </p>
                  <p className="font-semibold text-campaignBlack text-lg pt-4 border-t-2 border-gray-200">
                    Driven by service, integrity, and results, Orlando Rivera is committed to putting the community first and delivering responsible, transparent leadership for Rochester.
                  </p>
                </div>
              </div>
            </div>
          </div>
          
          {/* Experience Gallery */}
          <div className="mt-16">
            <div className="text-center mb-12">
              <p className="text-sm font-bold uppercase tracking-widest text-campaignRed mb-2">Proven Leadership</p>
              <h3 className="font-oswald text-3xl md:text-5xl font-bold uppercase">In The Community</h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="relative group overflow-hidden rounded-sm">
                <img 
                  src="https://customer-assets.emergentagent.com/job_43db3809-a6c4-45ea-8f8f-7bfb185fd383/artifacts/92p96pl9_1338772597473334515.jpeg"
                  alt="Orlando Rivera engaging with community"
                  className="w-full h-80 object-cover shadow-lg group-hover:scale-105 transition-transform duration-500"
                  data-testid="gallery-photo-1"
                />
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-6">
                  <p className="text-white font-bold text-lg">Community Engagement</p>
                  <p className="text-gray-300 text-sm">Building relationships, one conversation at a time</p>
                </div>
              </div>
              <div className="relative group overflow-hidden rounded-sm">
                <img 
                  src="https://customer-assets.emergentagent.com/job_43db3809-a6c4-45ea-8f8f-7bfb185fd383/artifacts/fixwlbcr_299212622078057619.jpeg"
                  alt="Orlando Rivera speaking at event"
                  className="w-full h-80 object-cover shadow-lg group-hover:scale-105 transition-transform duration-500"
                  data-testid="gallery-photo-2"
                />
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-6">
                  <p className="text-white font-bold text-lg">Public Service</p>
                  <p className="text-gray-300 text-sm">Speaking up for what matters most</p>
                </div>
              </div>
              <div className="relative group overflow-hidden rounded-sm">
                <img 
                  src="https://customer-assets.emergentagent.com/job_43db3809-a6c4-45ea-8f8f-7bfb185fd383/artifacts/ayx1x71c_5872968690461939137.jpeg"
                  alt="Orlando Rivera with community members"
                  className="w-full h-80 object-cover shadow-lg group-hover:scale-105 transition-transform duration-500"
                  data-testid="gallery-photo-3"
                />
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-6">
                  <p className="text-white font-bold text-lg">Grassroots Leadership</p>
                  <p className="text-gray-300 text-sm">Meeting people where they are</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Issues Section */}
      <section id="issues" className="py-20 md:py-32 bg-campaignBlack text-white" data-testid="issues-section">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <h2 className="font-oswald text-4xl md:text-6xl font-bold uppercase text-center mb-16" data-testid="issues-heading">
            Where I Stand
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            
            {/* Public Safety */}
            <div className="issue-card border border-white/10 p-8" data-testid="issue-public-safety">
              <ShieldCheck className="w-12 h-12 text-campaignRed mb-6" />
              <h3 className="font-oswald text-2xl md:text-3xl font-bold uppercase mb-4">PUBLIC SAFETY FIRST</h3>
              <ul className="space-y-3 mb-4">
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-campaignRed mt-1 flex-shrink-0" />
                  <span>Fully support law enforcement and community policing</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-campaignRed mt-1 flex-shrink-0" />
                  <span>Repeal reckless bail laws that return repeat offenders to the street</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-campaignRed mt-1 flex-shrink-0" />
                  <span>Hold violent criminals accountable and invest in gang prevention</span>
                </li>
              </ul>
              <p className="italic text-gray-400">Law and order protects working families, seniors, and small businesses.</p>
            </div>

            {/* School Choice */}
            <div className="issue-card border border-white/10 p-8" data-testid="issue-school-choice">
              <GraduationCap className="w-12 h-12 text-campaignRed mb-6" />
              <h3 className="font-oswald text-2xl md:text-3xl font-bold uppercase mb-4">SCHOOL CHOICE & EDUCATION</h3>
              <ul className="space-y-3 mb-4">
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-campaignRed mt-1 flex-shrink-0" />
                  <span>Expand vouchers so families can choose charter schools</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-campaignRed mt-1 flex-shrink-0" />
                  <span>Ensure funding follows the student, not the system</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-campaignRed mt-1 flex-shrink-0" />
                  <span>End one-size-fits-all education mandates from Albany</span>
                </li>
              </ul>
              <p className="italic text-gray-400">Every child deserves a quality education, regardless of ZIP code.</p>
            </div>

            {/* Jobs & Economy */}
            <div className="issue-card border border-white/10 p-8" data-testid="issue-jobs">
              <Briefcase className="w-12 h-12 text-campaignRed mb-6" />
              <h3 className="font-oswald text-2xl md:text-3xl font-bold uppercase mb-4">JOBS & ECONOMIC GROWTH</h3>
              <ul className="space-y-3 mb-4">
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-campaignRed mt-1 flex-shrink-0" />
                  <span>Cut red tape and excessive regulations on small businesses</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-campaignRed mt-1 flex-shrink-0" />
                  <span>Tax relief for working families and entrepreneurs</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-campaignRed mt-1 flex-shrink-0" />
                  <span>Expand workforce training tied to skilled trades</span>
                </li>
              </ul>
              <p className="italic text-gray-400">Strong businesses create strong communities, not government programs.</p>
            </div>

            {/* Housing */}
            <div className="issue-card border border-white/10 p-8" data-testid="issue-housing">
              <Home className="w-12 h-12 text-campaignRed mb-6" />
              <h3 className="font-oswald text-2xl md:text-3xl font-bold uppercase mb-4">HOUSING & FISCAL RESPONSIBILITY</h3>
              <ul className="space-y-3 mb-4">
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-campaignRed mt-1 flex-shrink-0" />
                  <span>Support first-time homebuyer programs and responsible development</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-campaignRed mt-1 flex-shrink-0" />
                  <span>Fair, balanced housing laws for landlords and tenants</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-campaignRed mt-1 flex-shrink-0" />
                  <span>Fight wasteful spending and oppose Albany tax hikes on families</span>
                </li>
              </ul>
              <p className="italic text-gray-400">Government should live within its means, just like families do.</p>
            </div>

          </div>
        </div>
      </section>

      {/* Volunteer Section */}
      <section id="volunteer" className="py-20 md:py-32 bg-white" data-testid="volunteer-section">
        <div className="container mx-auto px-4 md:px-8 max-w-4xl">
          <div className="text-center mb-12">
            <h2 className="font-oswald text-4xl md:text-6xl font-bold uppercase mb-4" data-testid="volunteer-heading">Join Our Team</h2>
            <p className="text-gray-600 text-lg">Your support makes a difference. Volunteer today and help us build a better future for New York.</p>
          </div>
          <form onSubmit={handleVolunteerSubmit} className="space-y-6 bg-gray-50 p-8 md:p-12" data-testid="volunteer-form">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-bold uppercase tracking-wider mb-2">First Name *</label>
                <input 
                  type="text"
                  required
                  value={volunteerForm.first_name}
                  onChange={(e) => setVolunteerForm({...volunteerForm, first_name: e.target.value})}
                  className="w-full bg-transparent border-b-2 border-gray-300 focus:border-campaignRed px-0 py-3 focus:outline-none transition-colors"
                  data-testid="volunteer-first-name"
                />
              </div>
              <div>
                <label className="block text-sm font-bold uppercase tracking-wider mb-2">Last Name *</label>
                <input 
                  type="text"
                  required
                  value={volunteerForm.last_name}
                  onChange={(e) => setVolunteerForm({...volunteerForm, last_name: e.target.value})}
                  className="w-full bg-transparent border-b-2 border-gray-300 focus:border-campaignRed px-0 py-3 focus:outline-none transition-colors"
                  data-testid="volunteer-last-name"
                />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-bold uppercase tracking-wider mb-2">Email *</label>
                <input 
                  type="email"
                  required
                  value={volunteerForm.email}
                  onChange={(e) => setVolunteerForm({...volunteerForm, email: e.target.value})}
                  className="w-full bg-transparent border-b-2 border-gray-300 focus:border-campaignRed px-0 py-3 focus:outline-none transition-colors"
                  data-testid="volunteer-email"
                />
              </div>
              <div>
                <label className="block text-sm font-bold uppercase tracking-wider mb-2">Mobile Phone{volunteerForm.sms_opt_in && " *"}</label>
                <input 
                  type="tel"
                  required={volunteerForm.sms_opt_in}
                  value={volunteerForm.phone}
                  onChange={(e) => setVolunteerForm({...volunteerForm, phone: e.target.value})}
                  className="w-full bg-transparent border-b-2 border-gray-300 focus:border-campaignRed px-0 py-3 focus:outline-none transition-colors"
                  data-testid="volunteer-phone"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-bold uppercase tracking-wider mb-2">ZIP Code</label>
              <input 
                type="text"
                value={volunteerForm.zip_code}
                onChange={(e) => setVolunteerForm({...volunteerForm, zip_code: e.target.value})}
                className="w-full bg-transparent border-b-2 border-gray-300 focus:border-campaignRed px-0 py-3 focus:outline-none transition-colors"
                data-testid="volunteer-zip"
              />
            </div>
            <SmsConsent
              idPrefix="volunteer"
              checked={volunteerForm.sms_opt_in}
              onChange={(v) => setVolunteerForm({...volunteerForm, sms_opt_in: v})}
            />
            <button 
              type="submit"
              disabled={loading}
              className="cta-button w-full md:w-auto px-12 py-4 bg-campaignRed text-white font-oswald uppercase tracking-widest text-sm font-bold disabled:opacity-50"
              data-testid="volunteer-submit"
            >
              {loading ? "Submitting..." : "Sign Up to Volunteer"}
            </button>
          </form>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 md:py-32 bg-gray-50" data-testid="contact-section">
        <div className="container mx-auto px-4 md:px-8 max-w-4xl">
          <div className="text-center mb-12">
            <h2 className="font-oswald text-4xl md:text-6xl font-bold uppercase mb-4" data-testid="contact-heading">Get In Touch</h2>
            <p className="text-gray-600 text-lg">Have questions or want to support our campaign? Reach out today.</p>
          </div>
          <form onSubmit={handleContactSubmit} className="space-y-6 bg-white p-8 md:p-12 shadow-lg" data-testid="contact-form">
            <div>
              <label className="block text-sm font-bold uppercase tracking-wider mb-2">Name *</label>
              <input 
                type="text"
                required
                value={contactForm.name}
                onChange={(e) => setContactForm({...contactForm, name: e.target.value})}
                className="w-full bg-transparent border-b-2 border-gray-300 focus:border-campaignRed px-0 py-3 focus:outline-none transition-colors"
                data-testid="contact-name"
              />
            </div>
            <div>
              <label className="block text-sm font-bold uppercase tracking-wider mb-2">Email *</label>
              <input 
                type="email"
                required
                value={contactForm.email}
                onChange={(e) => setContactForm({...contactForm, email: e.target.value})}
                className="w-full bg-transparent border-b-2 border-gray-300 focus:border-campaignRed px-0 py-3 focus:outline-none transition-colors"
                data-testid="contact-email"
              />
            </div>
            <div>
              <label className="block text-sm font-bold uppercase tracking-wider mb-2">Subject</label>
              <input 
                type="text"
                value={contactForm.subject}
                onChange={(e) => setContactForm({...contactForm, subject: e.target.value})}
                className="w-full bg-transparent border-b-2 border-gray-300 focus:border-campaignRed px-0 py-3 focus:outline-none transition-colors"
                data-testid="contact-subject"
              />
            </div>
            <div>
              <label className="block text-sm font-bold uppercase tracking-wider mb-2">Mobile Phone{contactForm.sms_opt_in && " *"}</label>
              <input 
                type="tel"
                required={contactForm.sms_opt_in}
                value={contactForm.phone}
                onChange={(e) => setContactForm({...contactForm, phone: e.target.value})}
                className="w-full bg-transparent border-b-2 border-gray-300 focus:border-campaignRed px-0 py-3 focus:outline-none transition-colors"
                data-testid="contact-phone"
              />
            </div>
            <div>
              <label className="block text-sm font-bold uppercase tracking-wider mb-2">Message *</label>
              <textarea 
                required
                rows="6"
                value={contactForm.message}
                onChange={(e) => setContactForm({...contactForm, message: e.target.value})}
                className="w-full bg-transparent border-b-2 border-gray-300 focus:border-campaignRed px-0 py-3 focus:outline-none transition-colors resize-none"
                data-testid="contact-message"
              />
            </div>
            <SmsConsent
              idPrefix="contact"
              checked={contactForm.sms_opt_in}
              onChange={(v) => setContactForm({...contactForm, sms_opt_in: v})}
            />
            <button 
              type="submit"
              disabled={loading}
              className="cta-button w-full md:w-auto px-12 py-4 bg-campaignRed text-white font-oswald uppercase tracking-widest text-sm font-bold disabled:opacity-50"
              data-testid="contact-submit"
            >
              {loading ? "Sending..." : "Send Message"}
            </button>
          </form>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-campaignBlack text-gray-400 py-16" data-testid="footer">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-12 mb-12">
            <div>
              <img 
                src="https://customer-assets.emergentagent.com/job_rivera-ny-senate/artifacts/aasxup92_7815758719719681036.png"
                alt="Orlando Rivera Campaign Logo"
                className="h-20 md:h-24 w-auto mb-4 object-contain"
              />
              <p className="text-sm">Working for Our Future</p>
            </div>
            <div>
              <h4 className="font-oswald text-white font-bold uppercase mb-4">Quick Links</h4>
              <nav className="space-y-2">
                <button onClick={() => scrollToSection('about')} className="block hover:text-white transition-colors" data-testid="footer-about">About Orlando</button>
                <button onClick={() => scrollToSection('priorities')} className="block hover:text-white transition-colors" data-testid="footer-priorities">Our Priorities</button>
                <button onClick={() => scrollToSection('issues')} className="block hover:text-white transition-colors" data-testid="footer-issues">Issues</button>
                <button onClick={() => scrollToSection('volunteer')} className="block hover:text-white transition-colors" data-testid="footer-volunteer">Volunteer</button>
                <button onClick={() => scrollToSection('contact')} className="block hover:text-white transition-colors" data-testid="footer-contact">Contact</button>
              </nav>
            </div>
            <div data-testid="footer-legal-section">
              <h4 className="font-oswald text-white font-bold uppercase mb-4">Legal</h4>
              <nav className="space-y-2">
                <Link to="/terms" className="block hover:text-white transition-colors" data-testid="footer-terms">Terms &amp; Conditions</Link>
                <Link to="/privacy" className="block hover:text-white transition-colors" data-testid="footer-privacy">Privacy Policy</Link>
              </nav>
            </div>
            <div>
              <h4 className="font-oswald text-white font-bold uppercase mb-4">Get Involved</h4>
              <div className="space-y-4">
                <button 
                  onClick={() => scrollToSection('volunteer')}
                  className="block px-6 py-2 bg-campaignRed text-white font-bold uppercase text-sm hover:bg-red-700 transition-colors"
                  data-testid="footer-volunteer-button"
                >
                  Volunteer Now
                </button>
                <a 
                  href="https://secure.winred.com/orlando-for-senate/donate-today"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block px-6 py-2 border border-gray-600 text-white font-bold uppercase text-sm hover:border-white transition-colors text-center"
                  data-testid="footer-donate-button"
                >
                  Donate
                </a>
              </div>
            </div>
          </div>
          <div className="border-t border-gray-800 pt-8 text-center">
            <div className="inline-block bg-gray-900 border border-gray-700 px-6 py-3 rounded">
              <p className="text-xs text-gray-400">
                Paid for by Orlando for Senate
              </p>
            </div>
            <p className="text-xs text-gray-500 mt-4">
              © {new Date().getFullYear()} Rivera for NY Senate. All rights reserved.
            </p>
            <p className="text-xs text-gray-500 mt-2 space-x-4">
              <Link to="/terms" className="hover:text-white underline" data-testid="footer-bottom-terms">Terms &amp; Conditions</Link>
              <Link to="/privacy" className="hover:text-white underline" data-testid="footer-bottom-privacy">Privacy Policy</Link>
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="*" element={<HomePage />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
